/**
 * Planet Display Helper
 * DRY: Centralized logic for planet display, retrograde indication, and color styling
 * Used across: NakshatraAngaInfoTable, LakshmiDwadashamshaInfoTable, AshtamshaInfoTable
 */

export interface PlanetDisplay {
    shortName: string;
    isRetrograde: boolean;
    isHora: boolean;
    isChandra: boolean;
    isLagna: boolean;
    isRahuKetu: boolean;
    displayText: string;
    planetType: 'regular' | 'lagna' | 'hora' | 'chandra' | 'retrograde';
}

interface PlanetInfo {
    short_name: string;
    name: string;
    is_retrograde?: boolean;
}

/**
 * Create planet display object with all special properties
 * @param planet - Planet info from API
 * @param currentHoraPlanet - Current hora planet string (e.g., "Surya (19/24)")
 * @returns PlanetDisplay object with all display properties
 */
export function createPlanetDisplay(
    planet: PlanetInfo,
    currentHoraPlanet?: string | null
): PlanetDisplay {
    // Determine planet special properties
    const isLagna = planet.short_name === 'Lg';
    const isChandra = planet.short_name === 'Ch';

    // Rahu (Ra) and Ketu (Ke) are lunar nodes, always retrograde by nature
    // Don't show retrograde indication for them
    const isRahuKetu = planet.short_name === 'Ra' || planet.short_name === 'Ke';
    const isRetrograde = !isRahuKetu && (planet.is_retrograde || false);

    // Extract planet name from hora string (remove timing info like "(19/24)")
    const horaPlanetName = currentHoraPlanet ? currentHoraPlanet.split(' ')[0] : null;

    // Check if this planet is the current Hora planet
    // Compare both short_name and full name (API can return either format)
    const isHora = !!(horaPlanetName && (
        planet.short_name === horaPlanetName ||
        planet.name === horaPlanetName
    ));

    // Create display text: Sy for normal, (Sy) for retrograde
    // Note: Ra and Ke never show retrograde indication (always retrograde by nature)
    // Note: Lagna never shows retrograde indication
    let displayText = planet.short_name;
    if (isRetrograde && !isLagna) {
        displayText = `(${planet.short_name})`;
    }

    // Determine planet type for color configuration (priority order)
    let planetType: 'regular' | 'lagna' | 'hora' | 'chandra' | 'retrograde' = 'regular';

    if (isLagna) {
        planetType = 'lagna';
    } else if (isHora) {
        planetType = 'hora';
    } else if (isChandra) {
        planetType = 'chandra';
    } else if (isRetrograde) {
        planetType = 'retrograde';
    }

    return {
        shortName: planet.short_name,
        isRetrograde,
        isHora,
        isChandra,
        isLagna,
        isRahuKetu,
        displayText,
        planetType
    };
}

/**
 * Check if planet is Rahu or Ketu (lunar nodes)
 */
export function isRahuOrKetu(shortName: string): boolean {
    return shortName === 'Ra' || shortName === 'Ke';
}

/**
 * Get retrograde display text for a planet
 * DRY: Used by sprite updates during drag and static displays
 *
 * @param shortName - Planet short name (e.g., "Ma", "Bu")
 * @param baseDisplayName - Base display name (could be symbol or short name)
 * @param isRetrograde - Whether planet is retrograde (speed < 0)
 * @returns Display text with parentheses if retrograde
 */
export function getRetrogradeDisplayText(
    shortName: string,
    baseDisplayName: string,
    isRetrograde: boolean
): string {
    // Ra and Ke are always retrograde by nature - don't mark them
    // Lg (Lagna) is not a planet - never retrograde
    if (isRahuOrKetu(shortName) || shortName === 'Lg') {
        return baseDisplayName;
    }

    return isRetrograde ? `(${baseDisplayName})` : baseDisplayName;
}

/**
 * Check if planet is retrograde based on speed
 * DRY: Centralized retrograde detection from speed value
 *
 * @param shortName - Planet short name
 * @param speed - Planet speed (negative = retrograde)
 * @returns true if planet is retrograde
 */
export function isRetrogradePlanet(shortName: string, speed: number | undefined): boolean {
    // Ra and Ke are always retrograde - don't count them
    if (isRahuOrKetu(shortName) || shortName === 'Lg') {
        return false;
    }

    return speed !== undefined && speed < 0;
}